/**
 * Signatures, from our form to a carrier's signature pad.
 *
 * Our own pad (apps/frontend/src/components/fields/SignatureCanvas.tsx) records
 * what the agent drew two ways:
 *
 *   - the rasterised image, stored under the field's own id (`w9Signature`)
 *   - the raw stroke paths, stored under `signatureStrokesKey(fieldId)`
 *
 * Carrier pads such as Pinnacle's Konva one record pointer paths, not images,
 * so a signature has to be *drawn* onto them. Stored paths replay exactly.
 * Where there are none — a signature typed with the keyboard alternative is
 * rendered with fillText and has no strokes at all — the worker recovers paths
 * from the image instead, and those come back here to be fitted the same way.
 */
/**
 * Where the raw stroke paths for a SIGNATURE field live in formData.
 *
 * A sibling key rather than a structured field value: the field's own value
 * stays a plain data URL, which is what the W9 and EFT PDF generators, the
 * review screen, and validation all already expect.
 */
export function signatureStrokesKey(fieldId) {
    return `${fieldId}Strokes`;
}
/** Runtime shape check — formData is untyped JSON by the time the worker sees it. */
export function isCapturedSignature(value) {
    if (!value || typeof value !== "object")
        return false;
    const candidate = value;
    return (typeof candidate.width === "number" &&
        typeof candidate.height === "number" &&
        candidate.width > 0 &&
        candidate.height > 0 &&
        Array.isArray(candidate.strokes) &&
        candidate.strokes.some((stroke) => Array.isArray(stroke) && stroke.length >= 2));
}
/**
 * Scales stroke paths onto a destination pad: aspect ratio preserved, centred,
 * with a margin so the signature sits inside the box rather than against its
 * edges. Fits the *ink bounding box* rather than the source canvas, since a
 * signature rarely fills the canvas it was drawn on.
 *
 * Shared by both sources so a traced signature and a captured one land in
 * exactly the same place — which is what lets one be swapped for the other.
 */
export function fitSignatureToPad(paths, options) {
    const { padWidth, padHeight, maxPoints = 220, widthRatio = 0.88, heightRatio = 0.72 } = options;
    const usable = paths.filter((path) => path.length >= 2);
    if (!usable.length || padWidth <= 0 || padHeight <= 0)
        return [];
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const path of usable) {
        for (const point of path) {
            if (point.x < minX)
                minX = point.x;
            if (point.x > maxX)
                maxX = point.x;
            if (point.y < minY)
                minY = point.y;
            if (point.y > maxY)
                maxY = point.y;
        }
    }
    const inkWidth = Math.max(maxX - minX, 1);
    const inkHeight = Math.max(maxY - minY, 1);
    const scale = Math.min((padWidth * widthRatio) / inkWidth, (padHeight * heightRatio) / inkHeight);
    const offsetX = (padWidth - inkWidth * scale) / 2;
    const offsetY = (padHeight - inkHeight * scale) / 2;
    // Left to right, the way a signature is written.
    const ordered = [...usable].sort((a, b) => a[0].x - b[0].x);
    const total = ordered.reduce((sum, path) => sum + path.length, 0);
    const stride = Math.max(1, Math.ceil(total / maxPoints));
    return ordered
        .map((path) => path
        .filter((_, index) => index % stride === 0 || index === path.length - 1)
        .map((point) => ({
        fx: (offsetX + (point.x - minX) * scale) / padWidth,
        fy: (offsetY + (point.y - minY) * scale) / padHeight,
    })))
        .filter((path) => path.length >= 2);
}
